import { useEffect, useRef, useState } from "react";
import { ImageOff, RotateCcw } from "lucide-react";

const DELAYS = [1500, 4000, 9000];

// <img> that auto-retries with backoff when storage is briefly busy (503/429).
export const RetryImg = ({ src, className = "", alt = "", testId, ...rest }) => {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    setAttempt(0);
    setFailed(false);
    return () => clearTimeout(timer.current);
  }, [src]);

  const onError = () => {
    if (attempt >= DELAYS.length) { setFailed(true); return; }
    timer.current = setTimeout(() => setAttempt((a) => a + 1), DELAYS[attempt]);
  };

  const retry = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAttempt(0);
    setFailed(false);
  };

  if (failed) {
    return (
      <span
        role="button"
        tabIndex={0}
        aria-label="Image failed to load. Retry"
        onClick={retry}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") retry(e); }}
        className={`flex flex-col items-center justify-center gap-1 bg-slate-900 text-slate-400 text-[10px] cursor-pointer ${className}`}
        data-testid={testId ? `${testId}-failed` : undefined}
        title="Couldn't load image — click to retry"
      >
        <ImageOff className="w-4 h-4" />
        <span className="inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" />Retry</span>
      </span>
    );
  }

  const sep = src.includes("?") ? "&" : "?";
  const finalSrc = attempt ? `${src}${sep}r=${attempt}` : src;
  return <img src={finalSrc} alt={alt} className={className} onError={onError} data-testid={testId} {...rest} />;
};

export default RetryImg;
